import { ref, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { Artefact } from "@/lib/artefacts";
import { api, types } from "@/lib/openapi";
import router from "@/router";
import { defineEntityStore } from "@/stores/helpers";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useSyncStore } from "@/stores/sync-store";
import {
    discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";
import { throttle } from "lodash";
import { syncChannel } from "@/lib/broadcast-channels";

export type Flow = types.ProcessFlow;

// Uses the current route to determine what the 'flow' should be and fetches
// the flow description for you.
export const useFlowStore = defineEntityStore("flow", () => {
    const route = useRoute();
    const flow = ref<Flow>();
    const syncStore = useSyncStore();
    const entityId = computed(() => flow.value?.identity);
    const description = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.FLOW,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.FLOW);

    const { organization, stek, process } = useCurrentEntityManager();
    const isFetching = ref(false);
    const controller = ref(new AbortController());

    async function updateFlow(force = false) {
        const { flowId } = route.params;

        // In case the same flow is selected, return early.
        if (!force && flow.value && flow.value.identity === flowId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!flowId) {
            description.value = undefined;
            flow.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        flow.value = undefined;

        const { response, data } =
            await api.startProcessProcessElementMgtGetFlowStartPost({
                body: {
                    flow_id: `${flowId}`,
                    include_archived: true,
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            if (response.status === 404) {
                router.replace({
                    name: "not-found",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                    query: {
                        type: "flow",
                        id: flowId,
                    },
                });
                return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        flow.value = data;
        isFetching.value = false;

        if (flow.value) {
            await updateDescription();
        }
    }
    watch(
        () => route.params?.flowId,
        () => updateFlow(),
        { immediate: true }
    );

    const updateDescription = async () => {
        if (!flow.value) {
            return;
        }

        if (!description.value) {
            try {
                description.value = await getDescriptionArtefact(
                    flow.value,
                    "flowDescription",
                    controller.value.signal
                );
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }

        return !!description.value?.isModified;
    });

    const queueSave = async () => {
        if (!isModified.value || !flow.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateFlowStartPostData =
            {
                body: {
                    flow_id: flow.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.flow_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only flow_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.FLOW,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.FLOW &&
                identity.entityId === flow.value?.identity
            ) {
                updateFlow(true);
            }
        }
    );

    const deleteFlow = async () => {
        if (!flow.value) {
            return;
        }
        if (!flow.value.is_archived) {
            throw Error("Can only delete archived flows");
        }

        await api.startProcessProcessElementMgtDeleteFlowStartPost({
            body: {
                flow_id: flow.value.identity,
            },
        });

        if (organization.value && stek.value && process.value) {
            await router.push({
                name: "processPage",
                params: {
                    orgName: organization.value.name,
                    stekName: stek.value.name,
                    processId: process.value.identity,
                },
            });
        }
    };

    return {
        flow,
        description,
        isModified,
        isFetching,
        reset,
        deleteFlow,
    };
});

export const useFlowManager = () => storeToRefs(useFlowStore());
